/**
 * Online API client. Same-origin `/api` (the Vite dev proxy forwards to
 * `wrangler dev`; production serves client + Worker from one origin).
 * Every call degrades gracefully: network failure flips `online` to false and
 * surfaces as a null/err result — local play never depends on this module.
 */
import type { Replay } from '../engine/replay/replay';

export interface OnlineUser {
  id: number;
  handle: string;
  role: 'player' | 'moderator' | 'admin';
}

export interface LevelSummary {
  id: number;
  name: string;
  description: string;
  mode: 'normal' | 'platformer';
  songId: string;
  objectCount: number;
  authorDifficulty: number | null;
  communityDifficulty: number | null;
  revision: number;
  likes: number;
  downloads: number;
  author: string;
  createdAt: string;
}

export interface ScoreRow {
  handle: string;
  percent: number;
  timeMs: number;
  achievedAt: string;
}

export interface CloudSave {
  revision: number;
  data: string;
  updatedAt: string;
}

export type ApiResult<T> = { ok: true; value: T } | { ok: false; error: string; status: number };

export class OnlineClient {
  /** Last-known connectivity; flips false on any network failure. */
  online = true;
  user: OnlineUser | null = null;

  constructor(private readonly base = '/api') {}

  private async call<T>(
    path: string,
    init: RequestInit = {},
    parse: (body: Record<string, unknown>) => T = (b) => b as T,
    /** Extra non-2xx statuses whose bodies the parser understands (e.g. 409). */
    parseStatuses: number[] = [],
  ): Promise<ApiResult<T>> {
    let res: Response;
    try {
      res = await fetch(`${this.base}${path}`, {
        credentials: 'same-origin',
        headers: init.body ? { 'content-type': 'application/json' } : {},
        ...init,
      });
    } catch {
      this.online = false;
      return { ok: false, error: 'offline', status: 0 };
    }
    const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    // A 5xx without our JSON error envelope means the Worker never answered
    // (dead backend behind a live proxy/CDN) — treat as offline.
    if (res.status >= 500 && typeof body.error !== 'string') {
      this.online = false;
      return { ok: false, error: 'offline', status: res.status };
    }
    this.online = true;
    if (!res.ok && !parseStatuses.includes(res.status)) {
      return {
        ok: false,
        error: typeof body.message === 'string' ? body.message : String(body.error ?? res.status),
        status: res.status,
      };
    }
    return { ok: true, value: parse(body) };
  }

  // --- Auth ----------------------------------------------------------------------

  async me(): Promise<OnlineUser | null> {
    const r = await this.call('/auth/me', {}, (b) => b.user as OnlineUser);
    this.user = r.ok ? r.value : null;
    return this.user;
  }

  /** Username-first registration with security question/answer recovery. */
  async register(
    handle: string,
    password: string,
    securityQuestion: string,
    securityAnswer: string,
  ): Promise<ApiResult<OnlineUser>> {
    const r = await this.call(
      '/auth/register',
      {
        method: 'POST',
        body: JSON.stringify({ handle, password, securityQuestion, securityAnswer }),
      },
      (b) => b.user as OnlineUser,
    );
    if (r.ok) this.user = r.value;
    return r;
  }

  /** Sign in by user name (an "@" falls back to legacy email login). */
  async login(identifier: string, password: string): Promise<ApiResult<OnlineUser>> {
    const body = identifier.includes('@')
      ? { email: identifier, password }
      : { handle: identifier, password };
    const r = await this.call(
      '/auth/login',
      { method: 'POST', body: JSON.stringify(body) },
      (b) => b.user as OnlineUser,
    );
    if (r.ok) this.user = r.value;
    return r;
  }

  // --- Security-question password recovery -------------------------------------------

  securityQuestion(handle: string): Promise<ApiResult<string>> {
    return this.call(
      '/auth/security-question',
      { method: 'POST', body: JSON.stringify({ handle }) },
      (b) => b.question as string,
    );
  }

  verifyAnswer(handle: string, answer: string): Promise<ApiResult<unknown>> {
    return this.call('/auth/verify-answer', {
      method: 'POST',
      body: JSON.stringify({ handle, answer }),
    });
  }

  resetWithAnswer(handle: string, answer: string, password: string): Promise<ApiResult<unknown>> {
    return this.call('/auth/reset-with-answer', {
      method: 'POST',
      body: JSON.stringify({ handle, answer, password }),
    });
  }

  async logout(): Promise<void> {
    await this.call('/auth/logout', { method: 'POST' });
    this.user = null;
  }

  // --- Cloud saves -----------------------------------------------------------------

  getSave(): Promise<ApiResult<CloudSave | null>> {
    return this.call('/save', {}, (b) => b.save as CloudSave | null);
  }

  putSave(
    data: string,
    baseRevision: number | null,
  ): Promise<ApiResult<{ revision: number } | { conflict: CloudSave }>> {
    return this.call(
      '/save',
      { method: 'PUT', body: JSON.stringify({ data, baseRevision }) },
      (b) =>
        b.error === 'conflict'
          ? { conflict: b.server as CloudSave }
          : { revision: b.revision as number },
      [409], // conflict body carries the server copy for the chooser UI
    );
  }

  // --- Levels ----------------------------------------------------------------------

  publish(levelText: string, replay: Replay, description = ''): Promise<ApiResult<{ id: number }>> {
    return this.call(
      '/levels',
      { method: 'POST', body: JSON.stringify({ level: levelText, replay, description }) },
      (b) => ({ id: b.id as number }),
    );
  }

  browse(query: {
    sort?: string;
    search?: string;
    mode?: string;
    page?: number;
  }): Promise<ApiResult<{ levels: LevelSummary[]; hasMore: boolean }>> {
    const params = new URLSearchParams();
    if (query.sort) params.set('sort', query.sort);
    if (query.search) params.set('search', query.search);
    if (query.mode) params.set('mode', query.mode);
    if (query.page) params.set('page', String(query.page));
    return this.call(`/levels?${params}`, {}, (b) => ({
      levels: b.levels as LevelSummary[],
      hasMore: b.hasMore === true,
    }));
  }

  showcase(): Promise<
    ApiResult<{ daily: LevelSummary | null; weekly: LevelSummary | null; featured: LevelSummary[] }>
  > {
    return this.call('/showcase', {}, (b) => ({
      daily: b.daily as LevelSummary | null,
      weekly: b.weekly as LevelSummary | null,
      featured: b.featured as LevelSummary[],
    }));
  }

  /** Download raw level text (cached locally by the caller for offline play). */
  async download(id: number): Promise<ApiResult<string>> {
    let res: Response;
    try {
      res = await fetch(`${this.base}/levels/${id}/download`, { credentials: 'same-origin' });
    } catch {
      this.online = false;
      return { ok: false, error: 'offline', status: 0 };
    }
    if (!res.ok) return { ok: false, error: 'download-failed', status: res.status };
    return { ok: true, value: await res.text() };
  }

  like(id: number, on: boolean): Promise<ApiResult<unknown>> {
    return this.call(`/levels/${id}/like`, { method: on ? 'POST' : 'DELETE' });
  }

  vote(id: number, difficulty: number): Promise<ApiResult<{ communityDifficulty: number | null }>> {
    return this.call(
      `/levels/${id}/vote`,
      { method: 'POST', body: JSON.stringify({ difficulty }) },
      (b) => ({ communityDifficulty: b.communityDifficulty as number | null }),
    );
  }

  comments(
    id: number,
  ): Promise<ApiResult<Array<{ author: string; body: string; createdAt: string }>>> {
    return this.call(`/levels/${id}/comments`, {}, (b) => b.comments as never);
  }

  postComment(id: number, body: string): Promise<ApiResult<unknown>> {
    return this.call(`/levels/${id}/comments`, { method: 'POST', body: JSON.stringify({ body }) });
  }

  report(subjectType: string, subjectId: number, reason: string): Promise<ApiResult<unknown>> {
    return this.call('/reports', {
      method: 'POST',
      body: JSON.stringify({ subjectType, subjectId, reason }),
    });
  }

  submitScore(
    id: number,
    replay: Replay,
  ): Promise<ApiResult<{ improved: boolean; percent: number; timeMs: number }>> {
    return this.call(
      `/levels/${id}/scores`,
      { method: 'POST', body: JSON.stringify({ replay }) },
      (b) => b as { improved: boolean; percent: number; timeMs: number },
    );
  }

  leaderboard(id: number, scope: 'global' | 'friends'): Promise<ApiResult<ScoreRow[]>> {
    const q = scope === 'friends' ? '?scope=friends' : '';
    return this.call(`/levels/${id}/leaderboard${q}`, {}, (b) => b.scores as ScoreRow[]);
  }

  favorite(id: number, on: boolean): Promise<ApiResult<unknown>> {
    return this.call(`/levels/${id}/favorite`, { method: on ? 'POST' : 'DELETE' });
  }

  favorites(): Promise<ApiResult<LevelSummary[]>> {
    return this.call('/me/favorites', {}, (b) => b.levels as LevelSummary[]);
  }

  profile(handle: string): Promise<
    ApiResult<{
      handle: string;
      role: string;
      joinedAt: string;
      stats: { published: number; likes: number; downloads: number; clears: number };
      levels: LevelSummary[];
    }>
  > {
    return this.call(`/users/${encodeURIComponent(handle)}`, {}, (b) => b.profile as never);
  }

  block(handle: string, on: boolean): Promise<ApiResult<unknown>> {
    return this.call('/blocks', {
      method: on ? 'POST' : 'DELETE',
      body: JSON.stringify({ handle }),
    });
  }

  sendMessage(handle: string, body: string): Promise<ApiResult<unknown>> {
    return this.call('/messages', { method: 'POST', body: JSON.stringify({ handle, body }) });
  }

  inbox(): Promise<
    ApiResult<
      Array<{ id: number; sender: string; body: string; sentAt: string; readAt: string | null }>
    >
  > {
    return this.call('/messages', {}, (b) => b.messages as never);
  }

  markRead(id: number): Promise<ApiResult<unknown>> {
    return this.call(`/messages/${id}/read`, { method: 'POST' });
  }

  // --- Moderation (role-gated server-side) -------------------------------------------

  modReports(): Promise<
    ApiResult<
      Array<{
        id: number;
        subjectType: string;
        subjectId: number;
        reason: string;
        reporter: string;
      }>
    >
  > {
    return this.call('/mod/reports', {}, (b) => b.reports as never);
  }

  modResolve(id: number, resolution: string): Promise<ApiResult<unknown>> {
    return this.call(`/mod/reports/${id}/resolve`, {
      method: 'POST',
      body: JSON.stringify({ resolution }),
    });
  }

  modRemoveLevel(id: number, reason: string): Promise<ApiResult<unknown>> {
    return this.call(`/mod/levels/${id}/remove`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  modRemoveComment(id: number): Promise<ApiResult<unknown>> {
    return this.call(`/mod/comments/${id}/remove`, { method: 'POST', body: JSON.stringify({}) });
  }

  modFeature(id: number, featured: boolean): Promise<ApiResult<unknown>> {
    return this.call(`/mod/levels/${id}/feature`, {
      method: 'POST',
      body: JSON.stringify({ featured }),
    });
  }

  // Admin-only console endpoints (server re-checks the role on every call).

  modUsers(search: string): Promise<
    ApiResult<
      Array<{
        id: number;
        handle: string;
        role: string;
        createdAt: string;
        bannedAt: string | null;
      }>
    >
  > {
    return this.call(
      `/mod/users?search=${encodeURIComponent(search)}`,
      {},
      (b) => b.users as never,
    );
  }

  modBan(id: number, reason: string): Promise<ApiResult<unknown>> {
    return this.call(`/mod/users/${id}/ban`, { method: 'POST', body: JSON.stringify({ reason }) });
  }

  modUnban(id: number): Promise<ApiResult<unknown>> {
    return this.call(`/mod/users/${id}/unban`, { method: 'POST', body: JSON.stringify({}) });
  }

  /** Irreversible: account + published levels + blobs + social trail. */
  modDeleteUser(id: number): Promise<ApiResult<unknown>> {
    return this.call(`/mod/users/${id}/delete`, { method: 'POST', body: JSON.stringify({}) });
  }

  modSetRole(id: number, role: string): Promise<ApiResult<unknown>> {
    return this.call(`/mod/users/${id}/role`, { method: 'POST', body: JSON.stringify({ role }) });
  }

  modAudit(): Promise<
    ApiResult<
      Array<{
        id: number;
        actorId: number;
        action: string;
        subject: string;
        detail: string;
        createdAt: string;
      }>
    >
  > {
    return this.call('/mod/audit', {}, (b) => b.entries as never);
  }

  modSchedule(
    id: number,
    dailyOn: string | null,
    weeklyOn: string | null,
  ): Promise<ApiResult<unknown>> {
    return this.call(`/mod/levels/${id}/schedule`, {
      method: 'POST',
      body: JSON.stringify({ dailyOn, weeklyOn }),
    });
  }

  // --- Friends ---------------------------------------------------------------------

  friends(): Promise<ApiResult<Array<{ handle: string; status: string; outgoing: number }>>> {
    return this.call('/friends', {}, (b) => b.friends as never);
  }

  friendRequest(handle: string): Promise<ApiResult<unknown>> {
    return this.call('/friends/request', { method: 'POST', body: JSON.stringify({ handle }) });
  }

  friendAccept(handle: string): Promise<ApiResult<unknown>> {
    return this.call('/friends/accept', { method: 'POST', body: JSON.stringify({ handle }) });
  }
}
